import {
  Component,
  EventEmitter,
  HostListener,
  OnInit,
  Output,
  OnDestroy,
  SimpleChanges,
  ViewEncapsulation,
} from "@angular/core";
import Highcharts from "highcharts";
import { PharmaDataService } from "../pharma-data.service";
import {
  ColumnChartOptions,
  DrugsByMoaApiResponse,
  ChartStateColors,
  GlobalHighchartOptions,
} from "../pharma-page.model";
import { AppService } from "src/app/app.service";
import { Subscription } from "rxjs";
import { VAIconRegistryService, infoCircle } from "@va/core-library/icons";
import { Store } from "@ngrx/store";
import { FilterEventChanged } from "src/app/store/actions/pharma-page.actions";
import { AppState, stateGetter } from "src/app/store/state/app.state";

@Component({
  selector: "app-drugs-by-moa",
  templateUrl: "./drugs-by-moa.component.html",
  styleUrls: ["./drugs-by-moa.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class DrugsByMoaComponent implements OnInit, OnDestroy {
  chartFilters;
  facetFilters;
  highcharts = Highcharts;
  chartMoaOptions: Highcharts.Options;
  chartReady = false;
  showError = false;
  errorMessage = "";
  updateMoaFlag = false;
  chart: Highcharts.Chart;
  totalCount = 0;
  tickersInfo: any;
  selectedRank = 0;
  selectedMoa;
  lastCalled;
  categoryToIdMap = {};
  dataCallSubscription: Subscription;
  pharmaDatabasePageSubscriber: Subscription;
  gridSettings;
  @Output() onChartInstance: EventEmitter<Highcharts.Chart> =
    new EventEmitter();

  constructor(
    private service: PharmaDataService,
    private appService: AppService,
    private iconRegistry: VAIconRegistryService,
    private store: Store<AppState>,
  ) {
    this.iconRegistry.registerIcons([infoCircle]);
  }

  @HostListener("window:resize", [])
  sizeChange() {
    this.updateMoaFlag = true;
    if (this.chart) {
      setTimeout(() => {
        this.onChartInstance.emit(this.chart);
      }, 50);
    }
  }

  ngOnInit() {
    Highcharts.setOptions(GlobalHighchartOptions);
    this.service.onGlobalGridSettings.subscribe((resp) => {
      this.gridSettings = resp;
    });
    this.pharmaDatabasePageSubscriber = this.store
      .select(stateGetter("pharmaDatabasePage"))
      .subscribe((data: any) => {
        this.chartFilters = data.chartFilters;
        this.facetFilters = data.facetFilters;
        if (
          data.filterEvent.filterChanged &&
          data.filterEvent.filterChanged === "chart" &&
          !data.filterEvent.source.includes("moa")
        ) {
          this.facetFilters = data.lastFacetFilter;
        }
        if (
          data.filterEvent.filterChanged &&
          data.filterEvent.filterChanged !== "grid" &&
          data.filterEvent.source !== "moa-selected"
        ) {
          this.chartReady = false;
          this.getChartData();
        }
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      (changes.facetFilters && changes.facetFilters.currentValue) ||
      (changes.chartFilters &&
        changes.chartFilters.currentValue &&
        (this.callGetChartData(changes.chartFilters.currentValue) ||
          changes.chartFilters.isFirstChange()))
    ) {
      this.chartReady = false;
      this.getChartData();
    }
  }
  callGetChartData(currentChartFilters) {
    if (
      this.lastCalled &&
      this.lastCalled.chart_filter.moa === "" &&
      this.selectedRank > 10
    ) {
      return true;
    }
    if (
      this.lastCalled &&
      this.appService.compare(currentChartFilters, this.lastCalled.chart_filter)
    ) {
      return false;
    }

    return true;
  }
  getChartData() {
    const params = {
      chart_filter: { ...this.chartFilters },
      filter: { ...this.facetFilters },
      revenue_type: this.gridSettings
        ? this.gridSettings.options.data.revenue_type
        : "total_revenue",
    };
    this.lastCalled = params;
    if (this.dataCallSubscription) {
      this.dataCallSubscription.unsubscribe();
    }
    this.dataCallSubscription = this.service
      .getDrugsByMOA(params)
      .subscribe((response: DrugsByMoaApiResponse) => {
        if (response.status === "success") {
          this.convertDataToChartObj(response.data);
          this.totalCount = response.data.total;
          this.selectedRank = response.data.selected_moa_rank;
          this.selectedMoa = params.chart_filter.moa;
          this.showError = false;
          this.categoryToIdMap = response.data["values_mtag_map"];
        } else {
          this.errorMessage = response.message;
          this.showError = true;
        }

        setTimeout(() => {
          this.chartReady = true;
        }, 0);
      });
  }

  convertDataToChartObj(dataObj) {
    const categories = dataObj.values.map((val) => val[0]);
    const series = dataObj.values;
    this.chartMoaOptions = this.appService.superClone(ColumnChartOptions);
    this.chartMoaOptions.xAxis["categories"] = categories;
    this.chartMoaOptions.series[0]["data"] = this.setSelectedOnLoad(series);
    this.chartMoaOptions.plotOptions.series.events = {
      click: (event) => this.clickOnChart(event),
    };
  }

  clickOnChart(data) {
    if (data.point.y === 0) {
      //Block 0 count click on chart
      return;
    }
    const id = this.categoryToIdMap[data.point.category];
    if (id === this.chartFilters.moa) {
      this.chartFilters.moa = "";
      this.lastCalled.chart_filter.moa = "";

      this.chartMoaOptions.plotOptions.series.color = ChartStateColors.selected;
      //deselect all
      const series = this.chartMoaOptions.series[0]["data"];
      this.chartMoaOptions.series[0]["data"] = series.map((val) => {
        if (val.selected) val.selected = false;
        return val;
      });
      this.updateMoaFlag = true;
      this.store.dispatch(
        new FilterEventChanged({
          filterChanged: "chart",
          source: "moa-deselected",
          data: {
            chartFilters: this.chartFilters,
          },
        }),
      );
      return;
    }
    //update filter and emit event for other charts
    this.lastCalled.chart_filter.moa = id;
    this.chartFilters = JSON.parse(
      JSON.stringify({
        ...this.chartFilters,
        moa: id,
      }),
    );
    //change color of unselected bars
    this.store.dispatch(
      new FilterEventChanged({
        filterChanged: "chart",
        source: "moa-selected",
        data: {
          chartFilters: this.chartFilters,
          extraInfo: {
            moa_data: {
              [id]: data.point.category,
            },
          },
        },
      }),
    );
    //change color of unselected bars
    this.chartMoaOptions.plotOptions.series.color = ChartStateColors.deselected;
    this.updateMoaFlag = true;
  }
  setSelectedOnLoad(series) {
    if (this.chartFilters.moa) {
      const newSeries = series.map((val, index) => {
        const id = this.categoryToIdMap[val[0]];
        const obj = {
          y: val[1],
          x: index,
          selected: false,
        };
        if (id === this.chartFilters.moa) {
          obj.selected = true;
          return obj;
        }
        return obj;
      });
      this.chartMoaOptions.plotOptions.series.color =
        ChartStateColors.deselected;

      return newSeries;
    }
    return series;
  }

  ngOnDestroy() {
    if (this.dataCallSubscription) {
      this.dataCallSubscription.unsubscribe();
    }
    if (this.pharmaDatabasePageSubscriber) {
      this.pharmaDatabasePageSubscriber.unsubscribe();
    }
  }
}
